import "@/App.css";
import { useEffect, useRef } from "react";
import { MemoryRouter, Routes, Route, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Programs from "@/pages/Programs";
import Platform from "@/pages/Platform";
import Partners from "@/pages/Partners";
import FAQ from "@/pages/FAQ";
import Careers from "@/pages/Careers";
import Contact from "@/pages/Contact";
import Pharmacy from "@/pages/Pharmacy";
import Journey from "@/pages/Journey";
import Learn from "@/pages/Learn";
import Operations from "@/pages/Operations";
import Badge from "@/pages/Badge";
import BadgeAdmin from "@/pages/BadgeAdmin";
import Verify from "@/pages/Verify";
import InventoryPreview from "@/pages/InventoryPreview";
import InventoryAuthGate from "@/pages/inventory/InventoryAuthGate";
import PatientAdherence from "@/pages/PatientAdherence";
import Referrals from "@/pages/Referrals";
import Locker from "@/pages/Locker";
import PPAuthGate from "@/pages/pp/AuthGate";
import PPDashboard from "@/pages/pp/Dashboard";
import PPDetail from "@/pages/pp/PharmacyDetail";
import PPPatientDetail from "@/pages/pp/PatientDetail";
import ConsultAuthGate from "@/pages/consult/AuthGate";
import ConsultDashboard from "@/pages/consult/Dashboard";
import Marketing from "@/pages/Marketing";
import HopeWidget, { isPreviewHost as isHopePreviewHost } from "@/components/hope/HopeWidget";
import SeoManager from "@/components/SeoManager";

// Routes we accept as the initial deep-link. Anything else falls back to "/".
const KNOWN_ROUTES = new Set([
  "/",
  "/about",
  "/programs",
  "/platform",
  "/partners",
  "/faq",
  "/careers",
  "/contact",
  "/pharmacy",
  "/journey",
  "/learn",
  "/operations",
  "/badge",
  "/users/admin",
  "/verify",
  "/inventory-preview",
  "/inventory",
  "/inventory/dashboard",
  "/patient",
  "/referrals",
  "/locker",
  "/marketing",
  "/pharmacy/partners",
  "/pharmacy/partners/dashboard",
  "/consultations",
  "/consultations/dashboard",
  "/outreach",
  "/outreach/dashboard",
]);

// Resolve the entry route from window.location once at module load,
// then wipe the URL so the address bar stays at "/" (URL-masking preserved).
// Computed outside React so it's not affected by strict-mode double render.
// Belt-and-braces: if the URL is "/" but the user was recently deep inside the
// CRM (e.g. iOS opened a PDF and Back landed them on "/"), restore that path.
const CRM_LAST_PATH_KEY = "pp_last_path";
const CRM_LAST_PATH_TTL_MS = 30 * 60 * 1000; // 30 min

const INITIAL_ENTRY = (() => {
  if (typeof window === "undefined") return "/";
  const path = window.location.pathname;
  const search = window.location.search || "";
  const isDetail = /^\/pharmacy\/partners\/[^/]+(\/patients\/[^/]+)?$/.test(path);
  let entry =
    KNOWN_ROUTES.has(path) || isDetail ? path + search : "/";

  // If we landed at "/" but a fresh CRM/inventory path was saved, jump
  // back into that section (mobile Safari replaces the URL to "/" when
  // opening a blob PDF — this restores the deep-link on Back).
  if (entry === "/" && typeof sessionStorage !== "undefined") {
    try {
      const raw = sessionStorage.getItem(CRM_LAST_PATH_KEY);
      if (raw) {
        const { path: savedPath, ts } = JSON.parse(raw);
        const isRestorable =
          savedPath && (
            savedPath.startsWith("/pharmacy/partners") ||
            savedPath.startsWith("/inventory") ||
            savedPath.startsWith("/outreach") ||
            savedPath.startsWith("/consultations") ||
            savedPath.startsWith("/referrals")
          );
        if (isRestorable && Date.now() - ts < CRM_LAST_PATH_TTL_MS) {
          entry = savedPath;
        }
      }
    } catch { /* ignore parse errors */ }
  }

  if (window.history?.replaceState) {
    // Mask the address bar to "/" but record the real app path in the
    // history entry's state so Back/Forward can restore it (HistorySync).
    // Exception: /locker stays visible so staff/partners can bookmark it.
    window.history.replaceState({ appPath: entry }, "", entry === "/locker" ? "/locker" : "/");
  }
  return entry;
})();

// Keeps browser Back/Forward working inside the app while the address bar
// stays masked at "/": every in-app navigation is recorded as a browser
// history entry carrying the app path; popstate restores it in the router.
const HistorySync = () => {
  const location = useLocation();
  const nav = useNavigate();
  const navType = useNavigationType();

  useEffect(() => {
    const appPath = location.pathname + location.search;
    try {
      const url = appPath === "/locker" ? "/locker" : "/"; // /locker stays unmasked
      if (navType === "REPLACE") {
        // Preserve extra state fields (e.g. schedTab on the dashboard).
        window.history.replaceState({ ...(window.history.state || {}), appPath }, "", url);
      } else if (navType === "PUSH") {
        window.history.pushState({ appPath }, "", url);
      }
    } catch { /* ignore */ }
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onPop = (e) => {
      const p = e.state?.appPath;
      if (p) nav(p, { replace: true });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [nav]);

  return null;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  const prevPathRef = useRef(pathname);

  // Save scroll position for the pathname we're LEAVING so we can restore
  // it when the user comes back. Debounced via rAF so quick nav bursts
  // don't record 0 during unmount.
  useEffect(() => {
    const key = `pp-scroll:${prevPathRef.current}`;
    // Save the scroll position we're leaving from BEFORE React swaps the tree.
    try { sessionStorage.setItem(key, String(window.scrollY || 0)); } catch { /* ignore */ }

    // Restore for the new pathname (or scroll to top if we've never been).
    const targetKey = `pp-scroll:${pathname}`;
    let restored = 0;
    try { restored = Number(sessionStorage.getItem(targetKey) || 0); } catch { /* ignore */ }
    // Give React a tick to paint the new page before scrolling.
    const raf = requestAnimationFrame(() => {
      window.scrollTo({ top: restored, behavior: "instant" in window ? "instant" : "auto" });
    });

    prevPathRef.current = pathname;

    // Persist current CRM / outreach / inventory / referrals path so we can
    // restore it if the tab is bounced (e.g. iOS opens a PDF blob, then Back
    // returns to a fresh "/" load).
    if (typeof sessionStorage !== "undefined") {
      try {
        if (
          pathname.startsWith("/pharmacy/partners") ||
          pathname.startsWith("/inventory") ||
          pathname.startsWith("/outreach") ||
          pathname.startsWith("/consultations") ||
          pathname.startsWith("/referrals")
        ) {
          sessionStorage.setItem(
            CRM_LAST_PATH_KEY,
            JSON.stringify({ path: pathname, ts: Date.now() }),
          );
        }
      } catch { /* ignore */ }
    }
    return () => cancelAnimationFrame(raf);
  }, [pathname]);
  return null;
};

const Layout = ({ children }) => {
  const { pathname } = useLocation();
  // Hide Hope on admin/utility routes with heavy modal flows so the
  // floating avatar doesn't obscure action buttons (Submit / To max /
  // Save draft, etc.). Hope remains on public/marketing pages only.
  const HIDE_HOPE_ON = [
    "/inventory",
    "/operations",
    "/users/admin",
    "/badge",
    "/verify",
  ];
  const hideHope = HIDE_HOPE_ON.some((p) => pathname.startsWith(p));
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
      {/* Hope is temporarily hidden — re-enable by removing the `false &&` guard.
          Was: {isHopePreviewHost() && !hideHope && <HopeWidget />} */}
      {false && isHopePreviewHost() && !hideHope && <HopeWidget />}
    </div>
  );
};

const BareLayout = ({ children }) => (
  <div className="min-h-screen bg-[#F9F8F6]">{children}</div>
);

const PublicRoutes = () => (
  <Layout>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/programs" element={<Programs />} />
      <Route path="/platform" element={<Platform />} />
      <Route path="/partners" element={<Partners />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/pharmacy" element={<Pharmacy />} />
      <Route path="/learn" element={<Learn />} />
      <Route path="/operations" element={<Operations />} />
      <Route path="/badge" element={<Badge />} />
      <Route path="/users/admin" element={<BadgeAdmin />} />
      <Route path="/verify" element={<Verify />} />
      <Route path="/inventory-preview" element={<InventoryPreview />} />
      <Route path="/inventory" element={<InventoryAuthGate />} />
      <Route path="/inventory/dashboard" element={<InventoryPreview />} />
      <Route path="/patient" element={<PatientAdherence />} />
      <Route path="/referrals" element={<Referrals />} />
      <Route path="/locker" element={<Locker />} />
      <Route path="/marketing" element={<Marketing />} />
    </Routes>
  </Layout>
);

const JourneyRoutes = () => (
  <BareLayout>
    <Routes>
      <Route path="/journey" element={<Journey />} />
    </Routes>
  </BareLayout>
);

const PPRoutes = () => (
  <BareLayout>
    <div className="pp-scope">
      <Routes>
        <Route path="/pharmacy/partners" element={<PPAuthGate />} />
        <Route path="/pharmacy/partners/dashboard" element={<PPDashboard />} />
        <Route path="/pharmacy/partners/:id" element={<PPDetail />} />
        <Route path="/pharmacy/partners/:id/patients/:patientId" element={<PPPatientDetail />} />
      </Routes>
    </div>
  </BareLayout>
);

const ConsultRoutes = () => (
  <BareLayout>
    <div className="pp-scope">
      <Routes>
        {/* Canonical routes */}
        <Route path="/outreach" element={<ConsultAuthGate />} />
        <Route path="/outreach/dashboard" element={<ConsultDashboard />} />
        {/* Legacy alias — old /consultations deep links still work.
            (/scheduling retired 2026-09-12: it's no longer in KNOWN_ROUTES,
            so it falls back to the main website like any unknown path.) */}
        <Route path="/consultations" element={<ConsultAuthGate />} />
        <Route path="/consultations/dashboard" element={<ConsultDashboard />} />
      </Routes>
    </div>
  </BareLayout>
);

const AppRoot = () => {
  const { pathname } = useLocation();
  const isPP = pathname.startsWith("/pharmacy/partners");
  const isConsult = pathname.startsWith("/consultations") || pathname.startsWith("/outreach");

  // On CRM routes: (a) put .pp-scope on <body> so portaled dialogs can be
  // targeted by CRM-only CSS, and (b) update the viewport meta tag so iOS
  // Safari doesn't auto-zoom on input focus. maximum-scale=1 disables the
  // focus-zoom; pinch-to-zoom is still honored on iOS 10+ regardless.
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    const original = meta ? meta.getAttribute("content") : null;

    if (isPP || isConsult) {
      document.body.classList.add("pp-scope");
      if (meta) {
        meta.setAttribute(
          "content",
          "width=device-width, initial-scale=1, maximum-scale=1",
        );
      }
    } else {
      document.body.classList.remove("pp-scope");
    }

    return () => {
      document.body.classList.remove("pp-scope");
      if (meta && original) meta.setAttribute("content", original);
    };
  }, [isPP, isConsult]);

  if (isPP) return <PPRoutes />;
  if (isConsult) return <ConsultRoutes />;
  if (pathname.startsWith("/journey")) return <JourneyRoutes />;
  return <PublicRoutes />;
};

function App() {
  return (
    <div className="App">
      <MemoryRouter initialEntries={[INITIAL_ENTRY]}>
        <HistorySync />
        <ScrollToTop />
        <SeoManager />
        <AppRoot />
        <Toaster position="top-right" richColors />
      </MemoryRouter>
    </div>
  );
}

export default App;
